import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { adminApi, ApiError, type AdminUserRow } from '@/lib/api';
import { AdminModal, SearchBox, formatDate, DetailRow, EmptyState, TableSkeleton } from './AdminShared';

export function AdminUsers() {
  const { getToken, user: currentUser } = useAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<AdminUserRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.listUsers(token!, { search: search || undefined });
    },
  });

  const setSuperadmin = useMutation({
    mutationFn: async ({ id, value }: { id: string; value: boolean }) => {
      const token = await getToken();
      return adminApi.setSuperadmin(token!, id, value);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      setConfirmTarget(null);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to update user');
    },
  });

  const users = data?.users ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <SearchBox value={search} onChange={setSearch} placeholder="Search users…" />
        <span className="text-xs text-muted-foreground">{users.length} shown</span>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : users.length === 0 ? (
        <EmptyState message="No users found" />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-left">
                <th className="px-4 py-2.5 font-medium">User</th>
                <th className="px-4 py-2.5 font-medium">Email</th>
                <th className="px-4 py-2.5 font-medium text-center">Agencies</th>
                <th className="px-4 py-2.5 font-medium text-center">Role</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="border-b border-border/60 last:border-0 hover:bg-muted/40/50 transition-colors">
                    <td
                      className="px-4 py-2.5 font-medium text-foreground cursor-pointer"
                      onClick={() => setDetailId(u.id)}
                    >
                      {u.name ?? '—'}
                      {isSelf && <span className="ml-1.5 text-[10px] text-muted-foreground">(you)</span>}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground cursor-pointer" onClick={() => setDetailId(u.id)}>
                      {u.email}
                    </td>
                    <td className="px-4 py-2.5 text-center text-muted-foreground">{u.agency_count}</td>
                    <td className="px-4 py-2.5 text-center">
                      {u.is_superadmin === 1 ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-warning bg-warning/10 px-2 py-0.5 rounded-full">
                          <ShieldCheck className="h-3 w-3" /> Superadmin
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">User</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => {
                          setError(null);
                          setConfirmTarget(u);
                        }}
                        className={
                          u.is_superadmin === 1
                            ? 'inline-flex items-center gap-1 text-[11px] text-danger hover:bg-danger/10 px-2 py-1 rounded-md transition-colors'
                            : 'inline-flex items-center gap-1 text-[11px] text-foreground hover:bg-muted/40 px-2 py-1 rounded-md transition-colors'
                        }
                      >
                        {u.is_superadmin === 1 ? (
                          <>
                            <ShieldOff className="h-3 w-3" /> Revoke
                          </>
                        ) : (
                          <>
                            <ShieldCheck className="h-3 w-3" /> Make admin
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailId && <UserDetail id={detailId} onClose={() => setDetailId(null)} />}

      {confirmTarget && (
        <AdminModal
          title={confirmTarget.is_superadmin === 1 ? 'Revoke superadmin' : 'Grant superadmin'}
          onClose={() => setConfirmTarget(null)}
        >
          <p className="text-sm text-foreground">
            {confirmTarget.is_superadmin === 1 ? (
              <>
                Remove platform superadmin from <strong>{confirmTarget.email}</strong>? They will lose access to this admin
                panel.
              </>
            ) : (
              <>
                Grant platform superadmin to <strong>{confirmTarget.email}</strong>? They will gain full platform-wide
                control.
              </>
            )}
          </p>
          {error && <p className="text-xs text-danger mt-3">{error}</p>}
          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => setConfirmTarget(null)}
              className="px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground rounded-md hover:bg-muted/40"
            >
              Cancel
            </button>
            <button
              onClick={() =>
                setSuperadmin.mutate({ id: confirmTarget.id, value: confirmTarget.is_superadmin !== 1 })
              }
              disabled={setSuperadmin.isPending}
              className={
                confirmTarget.is_superadmin === 1
                  ? 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-danger text-danger-foreground rounded-md hover:bg-danger/90 disabled:opacity-50'
                  : 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent text-accent-foreground rounded-md hover:bg-accent/90 disabled:opacity-50'
              }
            >
              {setSuperadmin.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Confirm
            </button>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

function UserDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { getToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: async () => {
      const token = await getToken();
      return adminApi.getUser(token!, id);
    },
  });

  return (
    <AdminModal title={data?.user.name ?? data?.user.email ?? 'User'} onClose={onClose}>
      {isLoading || !data ? (
        <TableSkeleton rows={4} />
      ) : (
        <div className="space-y-5">
          <div className="border border-border rounded-lg p-3">
            <DetailRow label="Email">{data.user.email}</DetailRow>
            <DetailRow label="Name">{data.user.name ?? '—'}</DetailRow>
            <DetailRow label="Superadmin">{data.user.is_superadmin === 1 ? 'Yes' : 'No'}</DetailRow>
            <DetailRow label="User ID">
              <code className="text-[11px]">{data.user.id}</code>
            </DetailRow>
            <DetailRow label="Created">{formatDate(data.user.created_at)}</DetailRow>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-foreground mb-2">
              Agency memberships ({data.memberships.length})
            </h4>
            {data.memberships.length === 0 ? (
              <p className="text-xs text-muted-foreground">No memberships</p>
            ) : (
              <div className="space-y-1">
                {data.memberships.map((m) => (
                  <div
                    key={m.agency_id}
                    className="flex items-center justify-between text-xs bg-muted/40 rounded-md px-3 py-2"
                  >
                    <span className="text-foreground truncate">{m.agency_name}</span>
                    <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                      {m.role}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AdminModal>
  );
}
